import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton,
  MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { Confirm, DomainTabProps, ModeNotice, TabBody, dnsConfigOf, errorText } from './shared';

interface MailForward { id: number | string; source: string; destination: string }
interface UrlForward {
  hostname?: string; recordID?: string; destination?: string; redirectType?: string; redirectStructure?: string | number;
  pageTitle?: string; refreshSeconds?: number | string;
}

const URL_TYPES: Record<string, string> = { P: '301 permanent redirect', T: '302 temporary redirect', C: 'Cloaking', H: 'HTML redirect' };

const ForwardingTab: React.FC<DomainTabProps & { goTo?: (tab: string) => void }> = ({ serviceId, domain, info, goTo }) => {
  const mode = dnsConfigOf(info);
  const enabled = mode === null || mode === 2;
  const [mail, setMail] = useState<MailForward[]>([]);
  const [urls, setUrls] = useState<UrlForward[]>([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [dialog, setDialog] = useState<'mail' | 'url' | null>(null);
  const [mailDraft, setMailDraft] = useState({ source: '', destination: '' });
  const [urlDraft, setUrlDraft] = useState({ host_name: '', destination: 'https://', type: 'P', retain_path: true, wildcard_source: false });
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{ kind: 'mail' | 'url'; id: string; label: string } | null>(null);

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const [m, u] = await Promise.all([
        apiService.domainRequest<{ forwards: MailForward[] }>('get', serviceId, '/mail-forwards'),
        apiService.domainRequest<{ forwards: UrlForward[] }>('get', serviceId, '/url-forwards'),
      ]);
      setMail(m.forwards || []);
      setUrls(u.forwards || []);
    } catch (e) {
      setMsg({ severity: 'error', text: errorText(e, 'Could not load the forwards.') });
    } finally {
      setLoading(false);
    }
  }, [serviceId, enabled]);

  useEffect(() => { load(); }, [load]);

  const open = (kind: 'mail' | 'url') => { setDialogError(null); setDialog(kind); };

  const save = async () => {
    setSaving(true);
    setDialogError(null);
    try {
      if (dialog === 'mail') await apiService.domainRequest('post', serviceId, '/mail-forwards', mailDraft);
      else await apiService.domainRequest('post', serviceId, '/url-forwards', { ...urlDraft, host_name: urlDraft.host_name.trim() || null });
      setDialog(null);
      setMailDraft({ source: '', destination: '' });
      setMsg({ severity: 'success', text: 'Forward added.' });
      await load();
    } catch (e) {
      setDialogError(errorText(e, 'Could not add the forward.'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    setSaving(true);
    try {
      await apiService.domainRequest('delete', serviceId, `/${deleting.kind === 'mail' ? 'mail' : 'url'}-forwards/${encodeURIComponent(deleting.id)}`, undefined, { label: deleting.label });
      setDeleting(null);
      setMsg({ severity: 'success', text: 'Forward deleted.' });
      await load();
    } catch (e) {
      setDeleting(null);
      setMsg({ severity: 'error', text: errorText(e, 'Could not delete the forward.') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <TabBody>
      <ModeNotice current={mode} needs={2} what="Email and web forwards" onGoToNameservers={goTo ? () => goTo('nameservers') : undefined} />
      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {loading && <CircularProgress size={20} sx={{ mb: 1 }} />}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Email forwards</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => open('mail')} disabled={!enabled}>Add email forward</Button>
      </Box>
      <Paper variant="outlined" sx={{ mb: 3, overflow: 'auto', borderRadius: 0 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>From</TableCell><TableCell>To</TableCell><TableCell align="right" /></TableRow></TableHead>
          <TableBody>
            {mail.length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>No email forwards.</TableCell></TableRow>}
            {mail.map((f) => (
              <TableRow key={String(f.id)} hover>
                <TableCell>{f.source}</TableCell><TableCell>{f.destination}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting({ kind: 'mail', id: String(f.id), label: `${f.source} → ${f.destination}` })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2">Web (URL) forwards</Typography>
        <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => open('url')} disabled={!enabled}>Add web forward</Button>
      </Box>
      <Paper variant="outlined" sx={{ overflow: 'auto', borderRadius: 0 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>From</TableCell><TableCell>To</TableCell><TableCell>Type</TableCell><TableCell align="right" /></TableRow></TableHead>
          <TableBody>
            {urls.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>No web forwards.</TableCell></TableRow>}
            {urls.map((f, i) => (
              <TableRow key={f.recordID || i} hover>
                <TableCell>{f.hostname || domain}</TableCell>
                <TableCell sx={{ wordBreak: 'break-all' }}>{f.destination}</TableCell>
                <TableCell>{URL_TYPES[f.redirectType || ''] || f.redirectType || '—'}</TableCell>
                <TableCell align="right">
                  {f.recordID && (
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting({ kind: 'url', id: String(f.recordID), label: `${f.hostname || domain} → ${f.destination}` })}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!dialog} onClose={saving ? undefined : () => setDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{dialog === 'mail' ? 'Add email forward' : 'Add web forward'}</DialogTitle>
        <DialogContent dividers>
          {dialogError && <Alert severity="error" sx={{ mb: 2 }}>{dialogError}</Alert>}
          {dialog === 'mail' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Forward from" value={mailDraft.source} onChange={(e) => setMailDraft({ ...mailDraft, source: e.target.value })} placeholder={`sales@${domain}`} fullWidth />
              <TextField label="Forward to" value={mailDraft.destination} onChange={(e) => setMailDraft({ ...mailDraft, destination: e.target.value })} placeholder="someone@example.com" fullWidth />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField label="Forward from (optional)" value={urlDraft.host_name} onChange={(e) => setUrlDraft({ ...urlDraft, host_name: e.target.value })} placeholder={`www.${domain}`} helperText="Leave blank for the domain itself" fullWidth />
              <TextField label="Forward to" value={urlDraft.destination} onChange={(e) => setUrlDraft({ ...urlDraft, destination: e.target.value })} fullWidth />
              <TextField select label="Type" value={urlDraft.type} onChange={(e) => setUrlDraft({ ...urlDraft, type: e.target.value })} fullWidth>
                {Object.entries(URL_TYPES).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </TextField>
              <FormControlLabel control={<Checkbox checked={urlDraft.retain_path} onChange={(e) => setUrlDraft({ ...urlDraft, retain_path: e.target.checked })} />} label="Keep the page path (example.com/about → destination/about)" />
              <FormControlLabel control={<Checkbox checked={urlDraft.wildcard_source} onChange={(e) => setUrlDraft({ ...urlDraft, wildcard_source: e.target.checked })} />} label="Also forward every other sub-domain" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}>{saving ? <CircularProgress size={20} /> : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      <Confirm open={!!deleting} busy={saving} danger title="Delete this forward?" text={deleting?.label || ''} confirmLabel="Delete" onCancel={() => setDeleting(null)} onConfirm={remove} />
    </TabBody>
  );
};

export default ForwardingTab;
